/**
 * Authentication Store (Zustand)
 *
 * Global state management for authentication
 */

import { create } from "zustand";
import type { UserContext, Permission } from "@/types";
import authService from "@/services/auth-service";

// Define RoleType enum locally since it's not exported from types
enum RoleType {
  SUPER_ADMIN = "super_admin",
  ORG_ADMIN = "org_admin",
  TEAM_ADMIN = "team_admin",
  PROJECT_ADMIN = "project_admin",
  MEMBER = "member",
  VIEWER = "viewer",
}

interface AuthState {
  user: UserContext | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: UserContext) => void;
  setToken: (token: string) => void;
  login: () => void;
  logout: () => void;
  switchContext: (teamId?: string, projectId?: string) => Promise<void>;
  loadUser: () => Promise<void>;
  clearError: () => void;

  // Helper methods
  hasPermission: (permission: Permission) => boolean;
  hasRole: (role: RoleType) => boolean;
  isSuperAdmin: () => boolean;
  isOrgAdmin: () => boolean;
  isTeamAdmin: () => boolean;
  isProjectAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: authService.getStoredUser(),
  token: localStorage.getItem("auth_token"),
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,

  setUser: (user) => {
    set({ user, isAuthenticated: true, error: null });
  },

  setToken: (token) => {
    localStorage.setItem("auth_token", token);
    set({ token, isAuthenticated: true });
  },

  login: () => {
    authService.login();
  },

  logout: () => {
    authService.logout();
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null,
    });
  },

  switchContext: async (teamId, projectId) => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.switchContext(teamId, projectId);
      set({ user, isLoading: false });
    } catch (error) {
      set({
        error:
          error instanceof Error ? error.message : "Failed to switch context",
        isLoading: false,
      });
      throw error;
    }
  },

  loadUser: async () => {
    if (!authService.isAuthenticated()) {
      set({ isAuthenticated: false, user: null });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const user = await authService.getCurrentUser();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Failed to load user",
        isLoading: false,
        isAuthenticated: false,
        user: null,
      });
      authService.logout();
    }
  },

  clearError: () => {
    set({ error: null });
  },

  hasPermission: (permission) => {
    const { user } = get();
    if (!user) return false;
    // Super admin bypass
    if (user.global_role === RoleType.SUPER_ADMIN) return true;
    return user.permissions.includes(permission);
  },

  hasRole: (role) => {
    const { user } = get();
    if (!user) return false;
    return (
      user.global_role === role ||
      user.team_role === role ||
      user.project_role === role
    );
  },

  isSuperAdmin: () => {
    const { user } = get();
    if (!user) return false;
    return user.global_role === RoleType.SUPER_ADMIN;
  },

  isOrgAdmin: () => {
    const { user } = get();
    if (!user) return false;
    return (
      user.global_role === RoleType.SUPER_ADMIN ||
      user.global_role === RoleType.ORG_ADMIN
    );
  },

  isTeamAdmin: () => {
    const { user } = get();
    if (!user) return false;
    return (
      user.global_role === RoleType.SUPER_ADMIN ||
      user.global_role === RoleType.ORG_ADMIN ||
      user.team_role === RoleType.TEAM_ADMIN
    );
  },

  isProjectAdmin: () => {
    const { user } = get();
    if (!user) return false;
    return (
      user.global_role === RoleType.SUPER_ADMIN ||
      user.global_role === RoleType.ORG_ADMIN ||
      user.team_role === RoleType.TEAM_ADMIN ||
      user.project_role === RoleType.PROJECT_ADMIN
    );
  },
}));
